import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { C, withAlpha } from "../../constants/theme";
import { toast } from "../../hooks/useToast";
import { Btn } from "../ui/Btn";
import { novoRequestId } from "../../utils/mercadoPago";
import {
  PROVEDORES, AMBIENTES, criarCobrancaOnline, consultarCobrancaOnline, cancelarCobrancaOnline, comprovanteCobranca,
  linkDePagamentoSeguro, comprovanteSeguro, mensagemPagamento,
} from "../../utils/pagamentos";
import { PixMercadoPago } from "./PixMercadoPago";

/* "Cobrança online" dentro de Receber pagamento: o vendedor escolhe com qual
   provedor receber (só os que ele configurou) e a forma de pagamento.
   Este componente NUNCA marca o pedido como pago: quem faz isso é o
   servidor, depois de confirmar na API oficial do provedor. A tela só
   acompanha o espelho pedido.cobranca_online, que o servidor grava.
   O valor cobrado é o saldo do pedido, calculado no servidor. */

const TEXTO_SITUACAO = {
  aguardando: "⏳ Aguardando pagamento — a confirmação chega sozinha.",
  pago: "✅ Pagamento confirmado pelo provedor e registrado no pedido.",
  falhou: "❌ O pagamento foi recusado. Gere um novo link se o cliente quiser tentar de novo.",
  cancelada: "Cobrança cancelada: o link não aceita mais pagamento.",
  expirada: "O link expirou. Gere um novo.",
  estornado: "⚠️ O provedor informou estorno. Confira em Configurações → Pagamentos.",
  revisao: "⚠️ Este pagamento não foi lançado no pedido nem no caixa: precisa ser conferido. Veja o motivo em Configurações → Pagamentos.",
};

export function CobrancaOnline({ pedido, disponiveis, onPago }) {
  const [provedor, setProvedor] = useState(disponiveis[0] || null);
  const metodos = PROVEDORES[provedor]?.metodos || [];
  const [metodo, setMetodo] = useState(metodos[0]?.id || null);
  const [parcelas, setParcelas] = useState(12);
  const [cobranca, setCobranca] = useState(null);
  const [qr, setQr] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const requestId = useRef(novoRequestId());
  const avisado = useRef(false);

  useEffect(() => { setMetodo(PROVEDORES[provedor]?.metodos[0]?.id || null); setCobranca(null); requestId.current = novoRequestId(); }, [provedor]);
  // A lista chega depois (status lido do Firestore) ou muda: mantém uma escolha válida
  const listaDisponiveis = disponiveis.join(",");
  useEffect(() => {
    if (!listaDisponiveis.split(",").includes(provedor)) setProvedor(listaDisponiveis.split(",").filter(Boolean)[0] || null);
  }, [listaDisponiveis, provedor]);

  const espelho = pedido?.cobranca_online;
  const situacao = cobranca && espelho?.id === cobranca.cobrancaId ? espelho.situacao : cobranca?.situacao;
  const link = cobranca ? linkDePagamentoSeguro(cobranca.provedor, cobranca.link) : null;

  useEffect(() => {
    let atual = true;
    setQr("");
    if (link) QRCode.toDataURL(link, { width: 220, margin: 1 }).then(u => { if (atual) setQr(u); }).catch(() => {});
    return () => { atual = false; };
  }, [link]);

  useEffect(() => {
    if (situacao === "pago" && !avisado.current) { avisado.current = true; toast("✅ Pagamento confirmado pelo provedor.", "success"); onPago?.(); }
  }, [situacao, onPago]);

  if (!disponiveis.length) {
    return (
      <div style={{ fontSize:12, color:C.warning, lineHeight:1.6, background:withAlpha(C.warning, "12"), border:"1px solid "+withAlpha(C.warning, "44"), borderRadius:10, padding:"12px 14px", marginBottom:16 }}>
        Configure um provedor em <strong>Configurações → Pagamentos</strong> para cobrar online.
      </div>
    );
  }
  if (!PROVEDORES[provedor]) return null;

  const gerar = async () => {
    setOcupado(true);
    try {
      const r = await criarCobrancaOnline({ provedor, metodo, pedidoId: pedido.id, requestId: requestId.current, parcelasMax: metodo === "cartao" ? parcelas : undefined });
      setCobranca(r);
    } catch (e) {
      toast(mensagemPagamento(e), "error");
      requestId.current = novoRequestId();
    } finally { setOcupado(false); }
  };
  const verificar = async () => {
    setOcupado(true);
    try {
      const r = await consultarCobrancaOnline(cobranca.cobrancaId);
      if (r.situacao !== "pago") toast(r.semEvidencia ? "Ainda não chegou nenhuma notificação de pagamento deste link." : "Ainda não consta pagamento confirmado.", "info");
    } catch (e) { toast(mensagemPagamento(e), "error"); }
    finally { setOcupado(false); }
  };
  const cancelar = async () => {
    setOcupado(true);
    try { const r = await cancelarCobrancaOnline(cobranca.cobrancaId); setCobranca(c => ({ ...c, situacao: r.situacao })); }
    catch (e) { toast(mensagemPagamento(e), "error"); }
    finally { setOcupado(false); }
  };
  const copiar = async () => {
    try { await navigator.clipboard.writeText(link); toast("📋 Link copiado!", "success"); }
    catch { toast("Não foi possível copiar", "error"); }
  };
  const abrirComprovante = async () => {
    try {
      const { url } = await comprovanteCobranca(cobranca.cobrancaId);
      const seguro = comprovanteSeguro(url);
      if (seguro) window.open(seguro, "_blank", "noopener,noreferrer");
      else toast("Comprovante indisponível.", "info");
    } catch (e) { toast(mensagemPagamento(e), "error"); }
  };

  const botao = (ativo, cor) => ({ flex:1, padding:"9px 6px", borderRadius:10, fontSize:12, fontWeight:700, cursor:"pointer", background:ativo?withAlpha(cor, "22"):"transparent", border:"1px solid "+(ativo?cor:C.border), color:ativo?cor:C.muted });
  const rotulo = { color:C.muted, fontSize:10, fontWeight:700, letterSpacing:1.5, display:"block", marginBottom:6 };
  const emTeste = (provedor === "pagbank" && AMBIENTES.pagbank === "sandbox") || provedor === "infinitepay";

  return (
    <div style={{ marginBottom:16, display:"flex", flexDirection:"column", gap:10 }}>
      <div>
        <span style={rotulo}>RECEBER COM</span>
        <div role="radiogroup" aria-label="Receber com" style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          {disponiveis.map(id => (
            <button key={id} type="button" role="radio" aria-checked={provedor===id} disabled={ocupado}
              onClick={() => setProvedor(id)} style={botao(provedor===id, PROVEDORES[id].cor)}>{PROVEDORES[id].nome}</button>
          ))}
        </div>
      </div>

      {provedor === "mercadopago" ? (
        <PixMercadoPago pedido={pedido} conectado onPago={onPago} />
      ) : <>
        {emTeste && <div style={{ fontSize:11, color:C.warning }}>{provedor === "infinitepay" ? "InfinitePay — ambiente de teste / integração em desenvolvimento." : "PagBank Sandbox — ambiente de teste."}</div>}

        {!cobranca && <>
          <div>
            <span style={rotulo}>FORMA DE PAGAMENTO</span>
            <div role="radiogroup" aria-label="Forma de pagamento" style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {metodos.map(m => (
                <button key={m.id} type="button" role="radio" aria-checked={metodo===m.id} onClick={() => setMetodo(m.id)} style={botao(metodo===m.id, PROVEDORES[provedor].cor)}>{m.rotulo}</button>
              ))}
            </div>
          </div>
          {metodo === "cartao" && (
            <label style={{ fontSize:12, color:C.muted, display:"flex", alignItems:"center", gap:8 }}>
              Parcelar em até
              <select value={parcelas} onChange={e => setParcelas(Number(e.target.value))} style={{ background:C.card, color:C.text, border:"1px solid "+C.border, borderRadius:8, padding:"5px 8px" }}>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n}x</option>)}
              </select>
              <span>(juros do parcelamento por conta do cliente)</span>
            </label>
          )}
          <div style={{ fontSize:11.5, color:C.muted, lineHeight:1.5 }}>O valor cobrado é o saldo do pedido. O cliente paga na página oficial {PROVEDORES[provedor].doNome} — os dados do cartão não passam pelo Print Control.</div>
          <Btn color={PROVEDORES[provedor].cor} onClick={gerar} disabled={ocupado || !metodo}>{ocupado ? "Gerando…" : `🔗 Gerar link ${PROVEDORES[provedor].nome}`}</Btn>
        </>}

        {cobranca && <>
          <div style={{ textAlign:"center", fontSize:11, color:C.muted }}>Link de <strong style={{ color:C.text }}>R$ {Number(cobranca.valor).toFixed(2)}</strong> na sua conta {PROVEDORES[cobranca.provedor]?.nome}:</div>
          {link && situacao === "aguardando" && <>
            {qr ? <img src={qr} alt={`QR Code do link de pagamento ${PROVEDORES[cobranca.provedor]?.nome}`} style={{ width:200, height:200, alignSelf:"center", borderRadius:12, background:"#fff", padding:8, boxSizing:"border-box" }} />
              : <div style={{ fontSize:12, color:C.muted, textAlign:"center", padding:16 }}>⏳ Gerando QR Code…</div>}
            <div style={{ display:"flex", gap:6 }}>
              <Btn outline color={PROVEDORES[cobranca.provedor].cor} onClick={copiar} style={{ flex:1 }}>📋 Copiar link</Btn>
              <a href={link} target="_blank" rel="noopener noreferrer" style={{ flex:1, textAlign:"center", border:"1.5px solid "+PROVEDORES[cobranca.provedor].cor, color:PROVEDORES[cobranca.provedor].cor, borderRadius:8, padding:"9px 12px", fontSize:14, fontWeight:700, textDecoration:"none" }}>Abrir ↗</a>
            </div>
          </>}
          <div role="status" style={{ fontSize:12, color: situacao === "pago" ? C.success : C.muted, textAlign:"center", lineHeight:1.5 }}>{TEXTO_SITUACAO[situacao] || TEXTO_SITUACAO.aguardando}</div>
          {situacao !== "pago" && <div style={{ display:"flex", gap:6 }}>
            <Btn small outline onClick={verificar} disabled={ocupado} style={{ flex:1 }}>{ocupado ? "Consultando…" : "Verificar pagamento agora"}</Btn>
            {cobranca.provedor === "pagbank" && situacao === "aguardando" && <Btn small outline danger onClick={cancelar} disabled={ocupado}>Cancelar link</Btn>}
          </div>}
          {situacao === "pago" && cobranca.provedor === "infinitepay" && <Btn small outline onClick={abrirComprovante}>Ver comprovante</Btn>}
          {["cancelada", "expirada", "falhou"].includes(situacao) && <Btn small outline onClick={() => { setCobranca(null); requestId.current = novoRequestId(); }}>Gerar outro link</Btn>}
        </>}

        <div style={{ fontSize:11, color:C.muted, lineHeight:1.5 }}>
          🔒 O pedido só é marcado como pago depois que o servidor confirma o pagamento diretamente na API {PROVEDORES[provedor].doNome}.
        </div>
      </>}
    </div>
  );
}
